import * as React from "react";
import { Dialog as HeadlessDialog } from "@headlessui/react";
import { IconAlertCircle, IconCircleCheck, IconX } from "@tabler/icons-react";

import { Dialog } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * The dialog shell that matches the dashboard cards: a title row with a
 * close control, a hairline, the body, a hairline, and a footer with the
 * actions on the right. Rides the app's one scrim and z-index.
 */
export function ActionDialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  panelClassName,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  panelClassName?: string;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      panelClassName={cn("max-w-md", panelClassName)}
    >
      <div className="flex max-h-[90vh] flex-col">
        <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <HeadlessDialog.Title as="h2" className="text-[15px] font-semibold text-foreground">
              {title}
            </HeadlessDialog.Title>
            {description && (
              <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1.5 -mt-1 inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <IconX size={16} stroke={1.75} />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-4 border-t border-border px-5 py-3">
            {footer}
          </footer>
        )}
      </div>
    </Dialog>
  );
}

/** A quiet secondary action for a dialog footer, styled like the cards' text links. */
export function DialogTextButton({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        "rounded-sm text-[13px] font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
        className
      )}
      {...props}
    />
  );
}

/** An inline outcome line: what went wrong or what just happened, in the app's own colours. */
export function DialogNotice({
  tone,
  children,
}: {
  tone: "error" | "success" | "warning";
  children: React.ReactNode;
}) {
  const Icon = tone === "success" ? IconCircleCheck : IconAlertCircle;
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "flex items-start gap-2 rounded-md border px-3 py-2 text-[13px] text-foreground",
        tone === "error" && "border-destructive/40 bg-destructive/5",
        tone === "success" && "border-emerald-500/40 bg-emerald-500/5",
        tone === "warning" && "border-amber-500/40 bg-amber-500/5"
      )}
    >
      <Icon
        size={16}
        stroke={1.75}
        className={cn(
          "mt-0.5 shrink-0",
          tone === "error" && "text-destructive",
          tone === "success" && "text-emerald-500",
          tone === "warning" && "text-amber-500"
        )}
      />
      <div className="min-w-0">{children}</div>
    </div>
  );
}
